/**
 * Busca na API o que as telas precisam para montar os números de um mês:
 * as metas do mês, as vendas que valem (vendas_vigentes) daquela fonte e as
 * carteiras de clientes importadas (para o "% da carteira").
 * Enquanto recarrega O MESMO mês (ex.: depois de salvar), mantém os dados
 * anteriores (a tela não "pisca"). Ao trocar de mês, os dados do mês antigo
 * nunca aparecem com o nome do mês novo: `dados` fica null até chegar o novo.
 *
 * Lê: api.metas e api.vendas.
 * Usado por: telas/Painel.tsx, telas/Representante.tsx e telas/Cadastros.tsx.
 */
import { useEffect, useState } from 'react';
import { periodoDoMes } from '../dominio/fechamento.ts';
import type { Carteira, Fonte, Meta, Mes, VendaSalva } from '../dominio/tipos.ts';
import { api, ErroApi } from './api.ts';

export interface DadosDoMes {
  mes: Mes;
  fonte: Fonte;
  metas: Meta[];
  vendas: VendaSalva[];
  carteiras: Carteira[];
}

export interface EstadoDadosDoMes {
  dados: DadosDoMes | null;
  carregando: boolean;
  erro: ErroApi | null;
}

/** `versao` muda quando algo foi salvo e é preciso buscar de novo. */
export function usarDadosDoMes(mes: Mes, fonte: Fonte, versao = 0): EstadoDadosDoMes {
  const [estado, setEstado] = useState<EstadoDadosDoMes>({ dados: null, carregando: true, erro: null });

  useEffect(() => {
    let cancelado = false;
    setEstado((anterior) => ({ ...anterior, carregando: true, erro: null }));
    // O mês desta fonte (fechamento): em geral o calendário; Bauny 09/2026 vai até 01/10.
    const { inicio, fim } = periodoDoMes(fonte, mes);
    Promise.all([api.metas(mes), api.vendas(inicio, fim, fonte), api.carteiras()])
      .then(([metas, vendas, carteiras]) => {
        if (!cancelado) setEstado({ dados: { mes, fonte, metas: metas.metas, vendas: vendas.vendas, carteiras: carteiras.carteiras }, carregando: false, erro: null });
      })
      .catch((erro: unknown) => {
        if (cancelado) return;
        const erroApi = erro instanceof ErroApi ? erro : new ErroApi('APP', String(erro), 0, null);
        setEstado((anterior) => ({ ...anterior, carregando: false, erro: erroApi }));
      });
    return () => {
      cancelado = true;
    };
  }, [mes, fonte, versao]);

  const doMesPedido = estado.dados !== null && estado.dados.mes === mes && estado.dados.fonte === fonte;
  return doMesPedido ? estado : { ...estado, dados: null };
}
